import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { isSafeUrl } from '../../../utils/common';

export interface ViewerImage {
  url: string;
  fileName: string;
}

export interface ImageViewerProps {
  images: ViewerImage[];
  initialIndex?: number;
  onClose: () => void;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const btnSx = {
  color: 'common.white',
  bgcolor: 'rgba(255,255,255,0.1)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
};
const navBtnSx = {
  ...btnSx,
  position: 'absolute' as const,
  top: '50%',
  transform: 'translateY(-50%)',
  bgcolor: 'rgba(0,0,0,0.5)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
};

const Toolbar: React.FC<{
  fileName: string;
  url: string;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onClose: () => void;
}> = ({ fileName, url, zoom, onZoomIn, onZoomOut, onReset, onClose }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2,
      py: 1,
      flexShrink: 0,
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <Typography sx={{ fontSize: '13px', color: 'common.white', fontWeight: 500 }}>
      {fileName}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton size="small" onClick={onZoomOut} sx={btnSx} aria-label="Zoom out">
        <ZoomOutIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '12px', color: 'common.white', minWidth: 40, textAlign: 'center' }}
      >
        {Math.round(zoom * 100)}%
      </Typography>
      <IconButton size="small" onClick={onZoomIn} sx={btnSx} aria-label="Zoom in">
        <ZoomInIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton size="small" onClick={onReset} sx={btnSx} aria-label="Reset zoom">
        <RestartAltIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton
        size="small"
        // Guarded: the href comes from the API, and an unexpected scheme
        // (javascript:, data:) must not be opened.
        onClick={() => isSafeUrl(url) && window.open(url, '_blank', 'noopener,noreferrer')}
        sx={btnSx}
        aria-label="Open in new tab"
      >
        <OpenInNewIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton size="small" onClick={onClose} sx={btnSx} aria-label="Close viewer">
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  </Box>
);

const NavArrows: React.FC<{ onPrev: () => void; onNext: () => void }> = ({ onPrev, onNext }) => (
  <>
    <IconButton onClick={onPrev} sx={{ ...navBtnSx, left: 16 }} aria-label="Previous image">
      <ChevronLeftIcon sx={{ fontSize: 32 }} />
    </IconButton>
    <IconButton onClick={onNext} sx={{ ...navBtnSx, right: 16 }} aria-label="Next image">
      <ChevronRightIcon sx={{ fontSize: 32 }} />
    </IconButton>
  </>
);

const Dots: React.FC<{
  images: ViewerImage[];
  index: number;
  onSelect: (i: number) => void;
}> = ({ images, index, onSelect }) => (
  <Box
    sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.5, flexShrink: 0 }}
    onClick={(e) => e.stopPropagation()}
  >
    {images.map((img, i) => (
      <IconButton
        key={`${img.url}-${i}`}
        size="small"
        onClick={() => onSelect(i)}
        aria-label={`Go to image ${i + 1} of ${images.length}`}
        aria-current={i === index ? 'true' : undefined}
        sx={{ p: 0.75, '&:hover': { bgcolor: 'transparent' } }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: i === index ? 'common.white' : 'rgba(255,255,255,0.4)',
            transition: 'background-color 0.15s',
            '.MuiIconButton-root:hover &': {
              bgcolor: i === index ? 'common.white' : 'rgba(255,255,255,0.7)',
            },
          }}
        />
      </IconButton>
    ))}
  </Box>
);

export const ImageViewer: React.FC<ImageViewerProps> = ({ images, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const total = images.length;
  const current = images[index];
  const hasMultiple = total > 1;

  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM)), []);

  const goTo = useCallback(
    (nextIndex: number) => {
      setIndex(nextIndex);
      resetView();
    },
    [resetView]
  );

  const goNext = useCallback(() => {
    if (hasMultiple) goTo((index + 1) % total);
  }, [goTo, hasMultiple, index, total]);

  const goPrev = useCallback(() => {
    if (hasMultiple) goTo((index - 1 + total) % total);
  }, [goTo, hasMultiple, index, total]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      return Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setOffset((prev) => ({
      x: prev.x + e.clientX - lastPos.current.x,
      y: prev.y + e.clientY - lastPos.current.y,
    }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
      else if (e.key === '0') resetView();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, resetView, zoomIn, zoomOut, goNext, goPrev]);

  if (!current) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        bgcolor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={onClose}
    >
      <Toolbar
        fileName={current.fileName}
        url={current.url}
        zoom={zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={resetView}
        onClose={onClose}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          cursor: dragging.current ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <Box
          component="img"
          src={current.url}
          alt={current.fileName}
          draggable={false}
          sx={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging.current ? 'none' : 'transform 0.15s ease',
            userSelect: 'none',
          }}
        />
        {hasMultiple && <NavArrows onPrev={goPrev} onNext={goNext} />}
      </Box>

      {hasMultiple && <Dots images={images} index={index} onSelect={goTo} />}
    </Box>
  );
};
